export type ClinicianIdentityRole = 'physician' | 'care_coordinator' | 'lab_specialist';

export type ClinicianNoteAudience = 'clinician' | 'lab_internal';

export interface ClinicianIdentity {
  id: string;
  name: string;
  role: ClinicianIdentityRole;
  title: string;
  clinicIds: string[];
  administeredCourseIds: string[];
}

export interface ClinicianCareCourse {
  id: string;
  label: string;
  productContext: string;
  status: 'scheduled' | 'active' | 'monitoring' | 'complete';
  administeredByPhysicianId: string;
  nextMilestone: string;
  nextMilestoneAt: string;
}

export interface ClinicianPatientNote {
  id: string;
  audience: ClinicianNoteAudience;
  author: string;
  createdAt: string;
  body: string;
}

export interface ClinicianPatientFixture {
  id: string;
  code: string;
  initials: string;
  age: number;
  indication: string;
  clinicId: string;
  clinicName: string;
  primaryPhysicianId: string;
  risk: 'stable' | 'watch' | 'urgent';
  lastContactAt: string;
  courses: ClinicianCareCourse[];
  notes: ClinicianPatientNote[];
}

export interface ClinicianVisiblePatient extends Omit<ClinicianPatientFixture, 'notes'> {
  notes: ClinicianPatientNote[];
}

export interface ClinicianLogEntry {
  id: string;
  patientId: string;
  type: 'symptom' | 'follow_up' | 'dose' | 'message';
  label: string;
  detail: string;
  recordedAt: string;
}

export interface ClinicianScopedView {
  physician: ClinicianIdentity;
  patients: ClinicianVisiblePatient[];
  logEntries: ClinicianLogEntry[];
  today: {
    dueMilestones: Array<{
      patientId: string;
      patientCode: string;
      label: string;
      at: string;
      risk: ClinicianPatientFixture['risk'];
    }>;
    watchCount: number;
    activeCourseCount: number;
  };
}

const clinicianIdentities: ClinicianIdentity[] = [
  {
    id: 'phy-avery-chen',
    name: 'Avery Chen, MD',
    role: 'physician',
    title: 'Orthobiologics physician',
    clinicIds: ['clinic-north'],
    administeredCourseIds: ['course-oa-001', 'course-oa-002'],
  },
  {
    id: 'phy-maya-patel',
    name: 'Maya Patel, DO',
    role: 'physician',
    title: 'Sports medicine physician',
    clinicIds: ['clinic-south'],
    administeredCourseIds: ['course-tendon-001'],
  },
  {
    id: 'coord-rivera',
    name: 'Elena Rivera',
    role: 'care_coordinator',
    title: 'Care coordinator',
    clinicIds: ['clinic-north', 'clinic-south'],
    administeredCourseIds: [],
  },
  {
    id: 'lab-admin',
    name: 'Lab Operations',
    role: 'lab_specialist',
    title: 'Lab specialist',
    clinicIds: ['clinic-north', 'clinic-south'],
    administeredCourseIds: [],
  },
];

const patients: ClinicianPatientFixture[] = [
  {
    id: 'pat-001',
    code: 'PT-2401',
    initials: 'JR',
    age: 62,
    indication: 'Knee osteoarthritis',
    clinicId: 'clinic-north',
    clinicName: 'North Ridge Clinic',
    primaryPhysicianId: 'phy-avery-chen',
    risk: 'watch',
    lastContactAt: '2026-06-08T08:40:00Z',
    courses: [
      {
        id: 'course-oa-001',
        label: 'OA biologic series A',
        productContext: 'MSC-conditioned media',
        status: 'active',
        administeredByPhysicianId: 'phy-avery-chen',
        nextMilestone: 'Day 14 outcome check-in',
        nextMilestoneAt: '2026-06-08T16:00:00Z',
      },
    ],
    notes: [
      {
        id: 'note-001-a',
        audience: 'clinician',
        author: 'Avery Chen, MD',
        createdAt: '2026-06-07T19:15:00Z',
        body: 'Reports mild swelling after PT; no fever or escalating pain.',
      },
      {
        id: 'note-001-lab',
        audience: 'lab_internal',
        author: 'Lab QC',
        createdAt: '2026-06-07T20:10:00Z',
        body: 'Internal batch reconciliation note hidden from clinician view.',
      },
    ],
  },
  {
    id: 'pat-002',
    code: 'PT-2407',
    initials: 'AL',
    age: 48,
    indication: 'Hip osteoarthritis',
    clinicId: 'clinic-north',
    clinicName: 'North Ridge Clinic',
    primaryPhysicianId: 'phy-avery-chen',
    risk: 'stable',
    lastContactAt: '2026-06-06T14:20:00Z',
    courses: [
      {
        id: 'course-oa-002',
        label: 'OA biologic series B',
        productContext: 'Expanded MSC lot LA-24-18',
        status: 'monitoring',
        administeredByPhysicianId: 'phy-avery-chen',
        nextMilestone: 'Week 6 PROMIS survey',
        nextMilestoneAt: '2026-06-10T13:30:00Z',
      },
    ],
    notes: [
      {
        id: 'note-002-a',
        audience: 'clinician',
        author: 'Care team',
        createdAt: '2026-06-05T17:00:00Z',
        body: 'Mobility score improved two points since baseline.',
      },
    ],
  },
  {
    id: 'pat-003',
    code: 'PT-2414',
    initials: 'KM',
    age: 35,
    indication: 'Achilles tendinopathy',
    clinicId: 'clinic-south',
    clinicName: 'South Valley Clinic',
    primaryPhysicianId: 'phy-maya-patel',
    risk: 'urgent',
    lastContactAt: '2026-06-08T11:05:00Z',
    courses: [
      {
        id: 'course-tendon-001',
        label: 'Tendon recovery protocol',
        productContext: 'Platelet-derived exosome course',
        status: 'active',
        administeredByPhysicianId: 'phy-maya-patel',
        nextMilestone: 'Pain escalation call',
        nextMilestoneAt: '2026-06-08T18:30:00Z',
      },
    ],
    notes: [
      {
        id: 'note-003-a',
        audience: 'clinician',
        author: 'Maya Patel, DO',
        createdAt: '2026-06-08T11:05:00Z',
        body: 'New night pain. Call before end of day and review activity limits.',
      },
      {
        id: 'note-003-lab',
        audience: 'lab_internal',
        author: 'Lab Ops',
        createdAt: '2026-06-08T11:18:00Z',
        body: 'Internal viability annotation hidden from app fixtures.',
      },
    ],
  },
  {
    id: 'pat-004',
    code: 'PT-2418',
    initials: 'DS',
    age: 57,
    indication: 'Shoulder osteoarthritis',
    clinicId: 'clinic-east',
    clinicName: 'East Research Site',
    primaryPhysicianId: 'external-physician',
    risk: 'stable',
    lastContactAt: '2026-06-04T10:00:00Z',
    courses: [
      {
        id: 'course-external-001',
        label: 'External registry course',
        productContext: 'Registry-only biologic',
        status: 'complete',
        administeredByPhysicianId: 'external-physician',
        nextMilestone: 'Archived',
        nextMilestoneAt: '2026-06-15T15:00:00Z',
      },
    ],
    notes: [
      {
        id: 'note-004-a',
        audience: 'clinician',
        author: 'External site',
        createdAt: '2026-06-04T10:00:00Z',
        body: 'Should not appear for demo physicians without affiliation or course ownership.',
      },
    ],
  },
];

const logEntries: ClinicianLogEntry[] = [
  {
    id: 'log-001',
    patientId: 'pat-001',
    type: 'follow_up',
    label: 'Outcome check-in due',
    detail: 'Confirm swelling trend and update Day 14 symptoms.',
    recordedAt: '2026-06-08T16:00:00Z',
  },
  {
    id: 'log-002',
    patientId: 'pat-002',
    type: 'message',
    label: 'Patient message reviewed',
    detail: 'Asked about return-to-cycling timing.',
    recordedAt: '2026-06-07T12:20:00Z',
  },
  {
    id: 'log-003',
    patientId: 'pat-003',
    type: 'symptom',
    label: 'Night pain reported',
    detail: 'Escalated for same-day physician call.',
    recordedAt: '2026-06-08T11:05:00Z',
  },
];

export function getDemoPhysicians(): ClinicianIdentity[] {
  return clinicianIdentities.filter((identity) => identity.role === 'physician');
}

export function getDefaultDemoPhysician(): ClinicianIdentity {
  const physician = getDemoPhysicians()[0];
  if (!physician) {
    throw new Error('Clinician demo fixtures require at least one physician identity.');
  }
  return physician;
}

function isPatientVisibleToPhysician(
  patient: ClinicianPatientFixture,
  physician: ClinicianIdentity,
): boolean {
  const sharesClinicAffiliation = physician.clinicIds.includes(patient.clinicId);
  const hasAdministeredCourse = patient.courses.some(
    (course) =>
      course.administeredByPhysicianId === physician.id ||
      physician.administeredCourseIds.includes(course.id),
  );

  return (
    sharesClinicAffiliation &&
    (patient.primaryPhysicianId === physician.id || hasAdministeredCourse)
  );
}

function hideLabInternalNotes(patient: ClinicianPatientFixture): ClinicianVisiblePatient {
  return {
    ...patient,
    notes: patient.notes.filter((note) => note.audience === 'clinician'),
  };
}

export function getClinicianScopedView(physicianId: string): ClinicianScopedView {
  const physician =
    getDemoPhysicians().find((identity) => identity.id === physicianId) ??
    getDefaultDemoPhysician();
  const scopedPatients = patients
    .filter((patient) => isPatientVisibleToPhysician(patient, physician))
    .map(hideLabInternalNotes);
  const scopedPatientIds = new Set(scopedPatients.map((patient) => patient.id));
  const scopedLogs = logEntries.filter((entry) => scopedPatientIds.has(entry.patientId));
  const dueMilestones = scopedPatients.flatMap((patient) =>
    patient.courses
      .filter((course) => course.status === 'active' || course.status === 'monitoring')
      .map((course) => ({
        patientId: patient.id,
        patientCode: patient.code,
        label: course.nextMilestone,
        at: course.nextMilestoneAt,
        risk: patient.risk,
      })),
  );

  return {
    physician,
    patients: scopedPatients,
    logEntries: scopedLogs,
    today: {
      dueMilestones: dueMilestones.sort((a, b) => Date.parse(a.at) - Date.parse(b.at)),
      watchCount: scopedPatients.filter((patient) => patient.risk !== 'stable').length,
      activeCourseCount: scopedPatients.reduce(
        (count, patient) =>
          count + patient.courses.filter((course) => course.status === 'active').length,
        0,
      ),
    },
  };
}

export function formatClinicianDateTime(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}
